import "./MeetingSidePanelPage.scss";

import {
  Alert,
  Button,
  ChevronStartIcon,
  ExclamationTriangleIcon,
  Flex,
  SplitButton,
  Text,
} from "@fluentui/react-northstar";
import { QuestionType, TeamsSurveyTypes } from "@teams/types";
import { QuestionnaireTemplateDTO, TeamInfoDTO } from "@teams/types/api";
import moment from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import { useUserInfo } from "../../../_shared/actions/useUserInfo";
import { PollyIcon } from "../../../_shared/components/PollyIcon";
import { TeamsLoader } from "../../../_shared/components/TeamsLoader";
import { ChannelSurveysOptions } from "../../../_shared/services/SurveyApi";
import { useTeamsContext } from "../../../_shared/state/TeamsContextProvider";
import { useInterval } from "../../../_shared/utils/useInterval";
import { userCanCreate } from "../../../_shared/utils/userHelpers";
import { PollyAuthoring } from "../SurveyAuthoring/PollyAuthoring";
import { SurveyTypeSelect } from "../SurveyAuthoring/SurveyTypeSelect";
import { TemplateSelect } from "../SurveyAuthoring/Templates/TemplateSelect";
import { MeetingEmptyState } from "./components/MeetingEmptyState";
import { ResponseLimitBanner } from "./components/ResponseLimitBanner";
import { SurveyDetailsPage } from "./components/SurveyDetailsPage";
import { useMeetingSurveys } from "./hooks/useMeetingSurveys";
import { SurveyCard } from "./SurveyCard";

type Props = {
  teamInfo: TeamInfoDTO;
};

export const MeetingSidePanelPage: React.FC<Props> = ({ teamInfo }) => {
  const { teamsContext } = useTeamsContext();
  const { user, loadUser } = useUserInfo();
  const {
    surveys,
    loadSurveys,
    isLoading,
    error: surveyError,
  } = useMeetingSurveys(teamsContext, (a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  const [hasLoaded, setHasLoaded] = useState<boolean>(false);
  const [historicalHours] = useState<number>(24);
  const [showSidePanelWindow, setShowSidePanelWindow] = useState<{
    component: React.ReactNode;
    label: React.ReactNode;
  }>();

  const surveyLoadOptions = useMemo((): ChannelSurveysOptions => {
    return {
      closeAt: moment().subtract(historicalHours, "hours").toDate(),
    };
  }, [historicalHours]);

  useEffect(() => {
    (async function () {
      await loadSurveys(surveyLoadOptions);
      setHasLoaded(true);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useInterval(() => {
    if (!isLoading) {
      loadSurveys(surveyLoadOptions, true);
    }
  }, 5000);

  useInterval(() => {
    if (!isLoading) {
      loadUser(true);
    }
  }, 15000);

  const showTemplateSelectionWindow = useCallback(() => {
    setShowSidePanelWindow({
      label: "Back",
      component: (
        <SelectTemplatePanel
          onClose={() => setShowSidePanelWindow(undefined)}
          onSelected={template =>
            setShowSidePanelWindow({
              label: "Back",
              component: (
                <NewSurveyEditor
                  template={template}
                  onClose={() => setShowSidePanelWindow(undefined)}
                  onCreated={async () => {
                    await loadSurveys(surveyLoadOptions);
                    setShowSidePanelWindow(undefined);
                  }}
                />
              ),
            })
          }
        />
      ),
    });
  }, [loadSurveys, surveyLoadOptions]);

  return (
    <div className="meeting-side-panel-page">
      <Flex column gap="gap.small" style={{ display: !showSidePanelWindow ? undefined : "none" }}>
        {teamInfo.meetingRole !== "Attendee" && hasLoaded && surveys.length > 0 && (
          <Flex>
            <Flex.Item push>
              <div>
                <SplitButton
                  disabled={!userCanCreate(user)}
                  primary
                  button={{
                    primary: true,
                    content: "Quick Create",
                  }}
                  onMainButtonClick={() =>
                    setShowSidePanelWindow({
                      label: "Back",
                      component: (
                        <NewSurveyEditor
                          onClose={() => setShowSidePanelWindow(undefined)}
                          onCreated={async () => {
                            await loadSurveys(surveyLoadOptions);
                            setShowSidePanelWindow(undefined);
                          }}
                          showTemplateButton={true}
                          onTemplateSelected={showTemplateSelectionWindow}
                        />
                      ),
                    })
                  }
                  menu={[
                    {
                      key: "template",
                      "data-tname": "meeting-side-panel-send-template",
                      content: "Use Template",
                      onClick: showTemplateSelectionWindow,
                    },
                  ]}
                />
              </div>
            </Flex.Item>
          </Flex>
        )}

        <ResponseLimitBanner />

        <TeamsLoader isLoading={!hasLoaded} loadingMessage="Loading Pollys...">
          {surveyError && (
            <Alert
              danger
              content="Error Loading Pollys"
              icon={<ExclamationTriangleIcon />}
              style={{ margin: "1rem" }}
            />
          )}
          {surveys.length === 0 && hasLoaded && teamInfo.meetingRole === "Attendee" && (
            <MeetingEmptyState
              size={100}
              header={
                <Flex column gap="gap.smaller">
                  <Text
                    align="center"
                    content={"No questions have been sent yet"}
                    size="large"
                    weight="bold"
                    as="div"
                  />
                </Flex>
              }
              subtext={
                <Text
                  align="center"
                  content={"Presenters may send questions that will appear here"}
                  as="div"
                />
              }
            />
          )}
          {teamInfo.meetingRole !== "Attendee" && hasLoaded && surveys.length === 0 && (
            <Flex column>
              <NewSurveyEditor
                onClose={() => setShowSidePanelWindow(undefined)}
                onCreated={async () => {
                  await loadSurveys(surveyLoadOptions);
                  setShowSidePanelWindow(undefined);
                }}
                showBackButton={true}
                showTemplateButton={true}
                onTemplateSelected={showTemplateSelectionWindow}
              />
            </Flex>
          )}
          {surveys.map((survey, index) => {
            if (!user.featureFlags.qna && survey.type === "qna") {
              return null;
            }
            return (
              <SurveyCard
                key={survey.id}
                survey={survey}
                teamsContext={teamsContext}
                isSideBar
                position={index + 1}
                maxRespondents={teamInfo.memberCount}
                onChanged={() => loadSurveys(surveyLoadOptions)}
                onViewResults={(survey, results) =>
                  setShowSidePanelWindow({
                    label: "Back",
                    component: <SurveyDetailsPage survey={survey} results={results} />,
                  })
                }
              />
            );
          })}
        </TeamsLoader>
      </Flex>

      {showSidePanelWindow && (
        <Flex column gap="gap.medium" className="meeting-side-panel-page__details-view">
          <Flex>
            <Button
              text
              styles={{ paddingLeft: 0, marginLeft: 0, padding: 0, minWidth: "auto" }}
              icon={<ChevronStartIcon />}
              content={<Text weight="bold" content={showSidePanelWindow.label} size="medium" />}
              onClick={() => setShowSidePanelWindow(undefined)}
            />
          </Flex>

          {showSidePanelWindow.component}
        </Flex>
      )}
    </div>
  );
};

type SidePanelWindowProps = {
  onClose: () => void;
};

type SelectTemplateProps = SidePanelWindowProps & {
  onSelected: (template: QuestionnaireTemplateDTO) => void;
};

const SelectTemplatePanel: React.FC<SelectTemplateProps> = ({ onSelected }) => {
  return (
    <TemplateSelect
      templateScope="all"
      templateActionLabel="Select"
      isCompact
      compactSettings={{ hideDetails: true, hideMenu: true, hideMediaIcon: true }}
      onSelected={onSelected}
    />
  );
};

type NewSurveyEditorProps = SidePanelWindowProps & {
  template?: {
    type: TeamsSurveyTypes;
    id: string;
    tenantId: string;
  };
  onCreated: () => void;
  onTemplateSelected?: () => void;
  showBackButton?: boolean;
  showTemplateButton?: boolean;
};

const NewSurveyEditor: React.FC<NewSurveyEditorProps> = ({
  template,
  onCreated,
  onTemplateSelected,
  showBackButton,
  showTemplateButton,
}) => {
  const [selectedType, setSelectedType] = useState<TeamsSurveyTypes | undefined>(template?.type);
  const [selectedQuestionType, setSelectedQuestionType] = useState<QuestionType | undefined>();
  const [questionTemplateId, setQuestionTemplateId] = useState<string | undefined>(template?.id);
  const [trackingSource, setTrackingSource] = useState<string | undefined>(
    template
      ? template.tenantId === "global"
        ? "in-meeting-template-global"
        : "in-meeting-template-personal"
      : undefined
  );

  if (!selectedType) {
    return (
      <Flex column>
        <SurveyTypeSelect
          onSelected={item => {
            setSelectedType(item.type);
            setSelectedQuestionType(item.questionType);
            setQuestionTemplateId(item.questionTemplateId);
            setTrackingSource("in-meeting-polly-type");
          }}
        />
        {showTemplateButton && (
          <Flex column>
            <Text
              align="center"
              className="meeting-side-panel-page__subtext"
              content="Or Explore"
            />
            <Flex
              gap="gap.medium"
              className="meeting-side-panel-page__card"
              onClick={() => {
                onTemplateSelected && onTemplateSelected();
              }}
            >
              <Flex className="meeting-side-panel-page__card-icon" hAlign="center" vAlign="center">
                <PollyIcon icon="templates-icon" size="large" ignoreTheme inheritColor />
              </Flex>
              <Flex.Item grow>
                <Text content={"Templates"} className="meeting-side-panel-page__card-label" />
              </Flex.Item>
            </Flex>
          </Flex>
        )}
      </Flex>
    );
  } else {
    return (
      <Flex column gap="gap.medium">
        {showBackButton && (
          <Flex>
            <Button
              text
              styles={{ paddingLeft: 0, marginLeft: 0, padding: 0, minWidth: "auto" }}
              icon={<ChevronStartIcon />}
              content={<Text weight="bold" content={"Back"} size="medium" />}
              onClick={() => setSelectedType(undefined)}
            />
          </Flex>
        )}

        <PollyAuthoring
          apiOnly
          type={selectedType}
          questionType={selectedQuestionType}
          questionTemplateId={questionTemplateId}
          templateId={template?.id}
          onCreated={onCreated}
          authoringOrigin={"meetingSidePanel"}
          trackingSource={trackingSource ?? "unknown"}
          skipInstrumentation={false}
          isPersonal={false}
          formSettings={{
            compressed: true,
            allowScheduledSend: false,
            hideSaveAsTemplate: true,
          }}
        />
      </Flex>
    );
  }
};
